/**
 * Gas-day range picker for the Data Explorer (DATA_VISUALIZATION_FRONTEND_PLAN
 * §2.1). Start/end day inputs + presets, with the server's ≤ 370-day cap
 * enforced client-side. Days are Pacific gas-days — labeled so nobody reads
 * them as UTC.
 */
import { Button, Group, Stack, Text, TextInput } from "@mantine/core";
import { IconCalendar } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
  MAX_RANGE_DAYS,
  addDaysIso,
  dayCount,
  startOfMonth,
  todayPacific,
} from "../../lib/dateRange";

export type DayRange = { start: string; end: string };
export type RangePreset = { label: string; range: DayRange };

type Props = {
  value: DayRange;
  onChange: (range: DayRange) => void;
  /** Extra presets shown first (e.g. the batch period when arriving from a batch). */
  extraPresets?: RangePreset[];
};

function defaultPresets(): RangePreset[] {
  const today = todayPacific();
  const lastMonthEnd = addDaysIso(startOfMonth(today), -1);
  return [
    { label: "This month", range: { start: startOfMonth(today), end: today } },
    {
      label: "Last month",
      range: { start: startOfMonth(lastMonthEnd), end: lastMonthEnd },
    },
    { label: "Last 90 days", range: { start: addDaysIso(today, -89), end: today } },
  ];
}

function validate(start: string, end: string): string | null {
  if (!start || !end) return null;
  if (start > end) return "Start must be on or before end.";
  if (dayCount(start, end) > MAX_RANGE_DAYS)
    return `Range must be ${MAX_RANGE_DAYS} days or fewer.`;
  return null;
}

export function PeriodRangePicker({ value, onChange, extraPresets = [] }: Props) {
  const [start, setStart] = useState(value.start);
  const [end, setEnd] = useState(value.end);

  // Keep local inputs in sync when the range is changed elsewhere (preset in a
  // sibling, URL navigation, batch-derived default).
  useEffect(() => {
    setStart(value.start);
    setEnd(value.end);
  }, [value.start, value.end]);

  const error = validate(start, end);
  const count = error ? null : dayCount(start, end);
  const presets = [...extraPresets, ...defaultPresets()];

  const apply = (nextStart: string, nextEnd: string) => {
    setStart(nextStart);
    setEnd(nextEnd);
    if (!validate(nextStart, nextEnd) && nextStart && nextEnd) {
      onChange({ start: nextStart, end: nextEnd });
    }
  };

  const isActive = (r: DayRange) => r.start === value.start && r.end === value.end;

  return (
    <Stack gap="xs">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <TextInput
          type="date"
          label="Start"
          value={start}
          max={end || undefined}
          leftSection={<IconCalendar size={16} />}
          onChange={(e) => apply(e.currentTarget.value, end)}
          error={error ? " " : undefined}
        />
        <TextInput
          type="date"
          label="End"
          value={end}
          min={start || undefined}
          leftSection={<IconCalendar size={16} />}
          onChange={(e) => apply(start, e.currentTarget.value)}
          error={error ? " " : undefined}
        />
        <Group gap={6} pb={2}>
          {presets.map((p) => (
            <Button
              key={p.label}
              size="compact-sm"
              variant={isActive(p.range) ? "filled" : "default"}
              onClick={() => apply(p.range.start, p.range.end)}
            >
              {p.label}
            </Button>
          ))}
        </Group>
      </Group>
      <Group gap="sm">
        <Text size="xs" c="dimmed">
          PT gas-days
        </Text>
        {error ? (
          <Text size="xs" c="red">
            {error}
          </Text>
        ) : count !== null ? (
          <Text size="xs" c="dimmed">
            {count} day{count === 1 ? "" : "s"} selected · max {MAX_RANGE_DAYS}
          </Text>
        ) : null}
      </Group>
    </Stack>
  );
}
